// components/use-case-details/use-case-workflow/use-case-workflow-example-card.tsx
// components/use-case-details/use-case-workflow/use-case-chat-showcase.tsx
"use client";

import { motion } from 'framer-motion';
import { Bot, MessageSquare,User } from 'lucide-react';
import React from 'react';

interface ResultItem {
  name: string;
  details: string[];
  metrics?: {
    key: string;
    value: string;
  }[];
  highlight?: boolean;
}

interface ChatShowcaseProps {
  title?: string;
  description?: string;
  userQuery: string;
  aiThinkingText?: string;
  aiResponseIntro?: string;
  results: ResultItem[];
  aiResponseOutro?: string;
  icon?: React.ReactNode;
  className?: string;
}

const ChatShowcase: React.FC<ChatShowcaseProps> = ({
  title = "Chat Interaction",
  description,
  userQuery,
  aiThinkingText = "Processing your request...",
  aiResponseIntro = "Here are the results based on your query:",
  results,
  aiResponseOutro,
  icon,
  className = "",
}) => {
  
  return (
    <motion.div
      className={`rounded-xl overflow-hidden shadow-2xl border border-gray-700 ${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-5 border-b border-gray-600">
        <div className="flex items-center">
          <div className="bg-gray-900/60 p-3 rounded-lg mr-4 shadow-inner">
            {icon || <MessageSquare size={20} className="text-blue-400" />}
          </div>
          <div>
            <h4 className="text-xl font-bold text-white">{title}</h4>
            {description && <p className="text-sm text-gray-300 mt-1">{description}</p>}
          </div>
        </div>
      </div>
      
      {/* Chat area with better background */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6">
        {/* Chat Messages */}
        <div className="max-w-5xl mx-auto space-y-8">
          {/* User Message */}
          <div className="flex justify-end">
            <div className="max-w-[90%] flex items-start">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4 rounded-2xl rounded-tr-none text-white shadow-lg">
                <p className="leading-relaxed">{userQuery}</p>
              </div>
              <div className="bg-gray-700 rounded-full p-2 ml-3 flex-shrink-0 shadow-md">
                <User size={18} className="text-white" />
              </div>
            </div>
          </div>
          
          {/* AI Response - Thinking */}
          <div className="flex justify-start">
            <div className="flex items-start">
              <div className="bg-gray-700 rounded-full p-2 mr-3 flex-shrink-0 shadow-md">
                <Bot size={18} className="text-purple-400" />
              </div>
              <div className="bg-gray-800/80 p-4 rounded-2xl rounded-tl-none max-w-[90%] shadow-lg backdrop-blur-sm">
                <p className="text-gray-300 text-sm mb-2">{aiThinkingText}</p>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse delay-100" />
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse delay-200" />
                </div>
              </div>
            </div>
          </div>
          
          {/* AI Response - Results */}
          <div className="flex justify-start">
            <div className="flex items-start w-full">
              <div className="bg-gray-700 rounded-full p-2 mr-3 flex-shrink-0 shadow-md">
                <Bot size={18} className="text-purple-400" />
              </div>
              <div className="bg-gray-800/90 p-6 rounded-2xl rounded-tl-none w-full shadow-lg backdrop-blur-sm">
                {aiResponseIntro && (
                  <div className="text-white mb-6 leading-relaxed">{aiResponseIntro}</div>
                )}
                
                <div className="space-y-5">
                  {results.map((result, index) => (
                    <motion.div
                      key={index}
                      className={`bg-gray-900/70 rounded-lg overflow-hidden shadow-lg ${
                        result.highlight ? 'border-l-2 border-green-500' : ''
                      }`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 + 0.2 }}
                    >
                      <div className="p-5">
                        <div className="font-medium text-white text-lg mb-3">
                          {result.highlight && (
                            <span className="inline-block bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded mr-2">
                              Recommended
                            </span>
                          )}
                          {result.name}
                        </div>
                        <div className="space-y-2">
                          {result.details.map((detail, detailIndex) => (
                            <div key={detailIndex} className="text-gray-300 text-sm leading-relaxed">{detail}</div>
                          ))}
                        </div>
                        
                        {/* Metrics grid */}
                        {result.metrics && result.metrics.length > 0 && (
                          <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-700/50">
                            {result.metrics.map((metric, metricIndex) => (
                              <div key={metricIndex} className="flex flex-col">
                                <span className="text-xs text-gray-400">{metric.key}</span>
                                <span className={`text-sm ${
                                  (parseInt(metric.value) > 85 || 
                                  metric.value === "A" || 
                                  metric.value === "A-") ? 
                                  "text-green-400 font-bold" : "text-white"
                                }`}>
                                  {metric.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {aiResponseOutro && (
                  <div className="mt-6 text-gray-300 leading-relaxed border-t border-gray-700/30 pt-4">
                    {aiResponseOutro}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatShowcase;

